/**
 * ProductEditPage — edição dos dados do produto.
 *
 * Edita apenas o "tronco" do produto (nome, descrição, imagem, URLs,
 * comissão). Catálogo, Planos, Estoque, Checkouts e Cupons são editados
 * inline pelos drawers da ProductDetailsPage.
 *
 * Rota: /products/:id/edit (id = UUID ou numérico)
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import { productsV2API } from '../../services/api/v2/products';
import { uploadImageToCloudinary, createImagePreview } from '../../utils/imageUpload';
import { parseApiError, scrollToFirstError } from '../../utils/apiErrors';
import Dropdown from '../../components/ui/Dropdown';
import './ProductEditPage.css';

const isUuid = (v) => typeof v === 'string' && v.includes('-');

const CATEGORIES = [
  'Administração e Negócios',
  'Arquitetura e Engenharia',
  'Artes e Música',
  'Casa e Jardinagem',
  'Comidas e Bebidas',
  'Direito',
  'Educacional, Cursos Técnicos',
  'Energético',
  'Entretenimento, Lazer e Diversão',
  'Esporte',
  'Idiomas',
  'Marketing Digital',
  'Saúde e Beleza',
  'Suplementos',
  'Tecnologia',
  'Outros',
];

const ProductEditPage = () => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [topError, setTopError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: '',
    image_url: '',
    image_preview: '',
    status: 'active',
    commission_type: 'percentage',
    commission_value_affiliate: 0,
    commission_value_manager: 0,
    commission_value_coproducer: 0,
    sales_page_url: '',
    thank_you_page_url: '',
    complaint_url: '',
    support_email: '',
    support_phone: '',
    warranty_days: 7,
    affiliate_auto_approval: false,
  });
  const [errors, setErrors] = useState({});

  // Carga inicial
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setTopError(null);
        const res = isUuid(routeId)
          ? await productsV2API.findByUuid(routeId)
          : await productsV2API.findById(routeId);
        const p = res.data?.product || res.data?.data || res.data;
        if (!alive) return;
        setOriginal(p);
        setForm({
          name: p.name || '',
          description: p.description || '',
          category: p.category || '',
          image_url: p.image_url || '',
          image_preview: p.image_url || '',
          status: p.status || 'active',
          commission_type: p.commission_type || 'percentage',
          commission_value_affiliate: Number(p.commission_value_affiliate) || 0,
          commission_value_manager: Number(p.commission_value_manager) || 0,
          commission_value_coproducer: Number(p.commission_value_coproducer) || 0,
          sales_page_url: p.sales_page_url || '',
          thank_you_page_url: p.thank_you_page_url || '',
          complaint_url: p.complaint_url || '',
          support_email: p.support_email || '',
          support_phone: p.support_phone || '',
          warranty_days: Number(p.warranty_days) || 7,
          affiliate_auto_approval: !!p.affiliate_auto_approval,
        });
      } catch (err) {
        if (alive) setTopError(err.response?.data?.message || err.message || 'Erro ao carregar produto');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [routeId]);

  const update = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: undefined }));
  };

  // Detecta mudanças (dirty state)
  const isDirty = useMemo(() => {
    if (!original) return false;
    const fields = [
      'name', 'description', 'category', 'image_url', 'status', 'commission_type',
      'commission_value_affiliate', 'commission_value_manager', 'commission_value_coproducer',
      'sales_page_url', 'thank_you_page_url', 'complaint_url',
      'support_email', 'support_phone', 'warranty_days', 'affiliate_auto_approval',
    ];
    return fields.some((f) => {
      const o = original[f] ?? '';
      const c = form[f] ?? '';
      return String(o) !== String(c);
    });
  }, [original, form]);

  const handleImageUpload = async (file) => {
    if (!file) return;
    try {
      setUploadingImage(true);
      setErrors((p) => ({ ...p, image_url: undefined }));
      const preview = await createImagePreview(file);
      update('image_preview', preview);
      const result = await uploadImageToCloudinary(file, { tags: ['product', 'pagmus'] });
      if (result.success) {
        update('image_url', result.url);
        update('image_preview', result.url);
      } else {
        throw new Error(result.error || 'Erro ao enviar imagem');
      }
    } catch (err) {
      setErrors((p) => ({ ...p, image_url: err.message }));
    } finally {
      setUploadingImage(false);
    }
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = 'Nome é obrigatório';
    if (!form.category) err.category = 'Categoria é obrigatória';
    if (form.support_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.support_email)) {
      err.support_email = 'Email inválido';
    }
    if (form.commission_type === 'percentage') {
      ['commission_value_affiliate', 'commission_value_manager', 'commission_value_coproducer'].forEach((f) => {
        const n = parseFloat(form[f]);
        if (form[f] !== '' && form[f] !== null && (isNaN(n) || n < 0 || n > 100)) err[f] = 'Entre 0 e 100%';
      });
    }
    if (form.warranty_days < 7) err.warranty_days = 'Mínimo 7 dias';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSave = async () => {
    if (saving) return;
    if (!validate()) {
      scrollToFirstError(errors);
      return;
    }
    try {
      setSaving(true);
      setTopError(null);
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        category: form.category,
        image_url: form.image_url || null,
        status: form.status,
        commission_type: form.commission_type,
        commission_value_affiliate: Number(form.commission_value_affiliate) || 0,
        commission_value_manager: Number(form.commission_value_manager) || 0,
        commission_value_coproducer: Number(form.commission_value_coproducer) || 0,
        sales_page_url: form.sales_page_url.trim() || null,
        thank_you_page_url: form.thank_you_page_url.trim() || null,
        complaint_url: form.complaint_url.trim() || null,
        support_email: form.support_email.trim() || null,
        support_phone: form.support_phone.trim() || null,
        warranty_days: Number(form.warranty_days) || 7,
        affiliate_auto_approval: !!form.affiliate_auto_approval,
      };
      const res = await productsV2API.update(original.id, payload);
      const saved = res.data?.product || res.data?.data || res.data;
      setOriginal(saved);
      setNotice({ type: 'success', message: 'Produto atualizado com sucesso!' });
      setTimeout(() => setNotice(null), 4000);
    } catch (err) {
      const { fieldErrors, message } = parseApiError(err, 'Erro ao salvar produto');
      if (Object.keys(fieldErrors).length) {
        setErrors((p) => ({ ...p, ...fieldErrors }));
        scrollToFirstError(fieldErrors);
      }
      setNotice({ type: 'error', message });
      setTimeout(() => setNotice(null), 6000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MasterLayout>
        <div className="pedit">
          <div className="pedit__center">
            <div className="pedit__spinner" />
            <span>Carregando produto...</span>
          </div>
        </div>
      </MasterLayout>
    );
  }

  if (topError && !original) {
    return (
      <MasterLayout>
        <div className="pedit">
          <div className="pedit__center">
            <Icon icon="mdi:alert-circle" width="40" style={{ color: '#ef4444' }} />
            <span style={{ color: '#991b1b' }}>{topError}</span>
            <button type="button" className="pedit__btn" onClick={() => navigate('/products')}>
              <Icon icon="mdi:arrow-left" width="14" /> Voltar à listagem
            </button>
          </div>
        </div>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <div className="pedit">
        <div className="pedit__wrap">

          {/* Topbar */}
          <div className="pedit__topbar">
            <button
              type="button"
              className="pedit__back"
              onClick={() => navigate(`/products/${original.uuid || original.id}`)}
            >
              <Icon icon="mdi:arrow-left" width="14" /> Voltar ao detalhe
            </button>
          </div>

          {/* Heading */}
          <div className="pedit__heading">
            <span className="pedit__heading-title">Editar Produto</span>
            <p className="pedit__heading-subtitle">
              Atualize os dados principais. Catálogo, planos e demais relações são editados dentro da página de detalhes.
            </p>
          </div>

          {/* Toast flutuante (sucesso / erro) */}
          {notice && (
            <div className={`pedit__toast pedit__toast--${notice.type}`}>
              <Icon
                icon={notice.type === 'success' ? 'mdi:check-circle' : 'mdi:alert-circle'}
                width="20"
                className="pedit__toast-icon"
              />
              <span className="pedit__toast-content">{notice.message}</span>
              <button
                type="button"
                className="pedit__toast-close"
                onClick={() => setNotice(null)}
                aria-label="Fechar"
              >
                <Icon icon="mdi:close" width="14" />
              </button>
            </div>
          )}
          {topError && original && (
            <div className="pedit__notice pedit__notice--error">
              <Icon icon="mdi:alert-circle" width="18" />
              <span>{topError}</span>
            </div>
          )}

          {/* Identidade */}
          <div className="pedit__card">
            <div className="pedit__card-header">
              <div className="pedit__card-icon">
                <Icon icon="mdi:package-variant" width="20" />
              </div>
              <div className="pedit__card-title-wrap">
                <span className="pedit__card-title">Identidade do produto</span>
                <span className="pedit__card-subtitle">Nome, categoria, descrição e capa</span>
              </div>
            </div>

            {/* Imagem */}
            <div className="pedit__field">
              <span className="pedit__label">Capa do produto</span>
              <div className="pedit__image">
                <div className="pedit__image-preview">
                  {form.image_preview ? (
                    <img src={form.image_preview} alt={form.name} />
                  ) : (
                    <Icon icon="mdi:image-outline" width="40" />
                  )}
                </div>
                <div className="pedit__image-actions">
                  <label className="pedit__upload-btn">
                    <Icon icon="mdi:upload" width="14" />
                    {uploadingImage ? 'Enviando...' : (form.image_url ? 'Trocar imagem' : 'Enviar imagem')}
                    <input
                      type="file"
                      accept="image/png, image/jpeg, image/jpg, image/webp"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleImageUpload(f);
                      }}
                    />
                  </label>
                  {form.image_url && (
                    <button
                      type="button"
                      className="pedit__upload-btn"
                      onClick={() => {
                        update('image_url', '');
                        update('image_preview', '');
                      }}
                    >
                      <Icon icon="mdi:trash-can-outline" width="14" />
                      Remover
                    </button>
                  )}
                  <span className="pedit__help">PNG, JPG ou WebP até 5MB</span>
                  {errors.image_url && (
                    <span className="pedit__error">
                      <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.image_url}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="pedit__field-row">
              <div className="pedit__field">
                <span className="pedit__label pedit__label--required">Nome do produto</span>
                <input
                  className="pedit__input"
                  type="text"
                  data-field="name"
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  placeholder="Ex: Curso Avançado de..."
                />
                {errors.name && (
                  <span className="pedit__error">
                    <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.name}
                  </span>
                )}
              </div>
              <div className="pedit__field">
                <span className="pedit__label pedit__label--required">Categoria</span>
                <Dropdown
                  placeholder="Selecione..."
                  value={form.category}
                  onChange={(v) => update('category', v)}
                  options={CATEGORIES.map((c) => ({ value: c, label: c }))}
                />
                {errors.category && (
                  <span className="pedit__error">
                    <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.category}
                  </span>
                )}
              </div>
            </div>

            <div className="pedit__field">
              <span className="pedit__label">Descrição</span>
              <textarea
                className="pedit__textarea"
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
                placeholder="Descreva o produto pra aparecer no checkout e na loja..."
              />
            </div>

            <div className="pedit__field-row">
              <div className="pedit__field">
                <span className="pedit__label">Status</span>
                <Dropdown
                  value={form.status}
                  onChange={(v) => update('status', v)}
                  options={[
                    { value: 'active', label: 'Ativo', icon: 'mdi:check-circle-outline' },
                    { value: 'paused', label: 'Pausado', icon: 'mdi:pause-circle-outline' },
                    { value: 'archived', label: 'Arquivado', icon: 'mdi:archive-outline' },
                  ]}
                />
                <span className="pedit__help">Pausado não aceita novas vendas. Arquivado some da loja.</span>
              </div>
              <div className="pedit__field">
                <span className="pedit__label">Garantia (dias)</span>
                <div className="pedit__input-suffix">
                  <input
                    className="pedit__input"
                    type="number"
                    min={7}
                    data-field="warranty_days"
                    value={form.warranty_days}
                    onChange={(e) => update('warranty_days', parseInt(e.target.value, 10) || 7)}
                  />
                  <span className="pedit__input-suffix-text">dias</span>
                </div>
                {errors.warranty_days && (
                  <span className="pedit__error">
                    <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.warranty_days}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* URLs & Suporte */}
          <div className="pedit__card">
            <div className="pedit__card-header">
              <div className="pedit__card-icon">
                <Icon icon="mdi:link-variant" width="20" />
              </div>
              <div className="pedit__card-title-wrap">
                <span className="pedit__card-title">URLs & Suporte</span>
                <span className="pedit__card-subtitle">Páginas externas e canais de atendimento</span>
              </div>
            </div>

            <div className="pedit__field-row">
              <div className="pedit__field">
                <span className="pedit__label">Página de Vendas</span>
                <input
                  className="pedit__input"
                  type="url"
                  data-field="sales_page_url"
                  value={form.sales_page_url}
                  onChange={(e) => update('sales_page_url', e.target.value)}
                  placeholder="https://..."
                />
                {errors.sales_page_url && (
                  <span className="pedit__error">
                    <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.sales_page_url}
                  </span>
                )}
              </div>
              <div className="pedit__field">
                <span className="pedit__label">Página de Agradecimento</span>
                <input
                  className="pedit__input"
                  type="url"
                  data-field="thank_you_page_url"
                  value={form.thank_you_page_url}
                  onChange={(e) => update('thank_you_page_url', e.target.value)}
                  placeholder="https://..."
                />
                {errors.thank_you_page_url && (
                  <span className="pedit__error">
                    <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.thank_you_page_url}
                  </span>
                )}
              </div>
            </div>

            <div className="pedit__field">
              <span className="pedit__label">Reclame Aqui</span>
              <input
                className="pedit__input"
                type="url"
                data-field="complaint_url"
                value={form.complaint_url}
                onChange={(e) => update('complaint_url', e.target.value)}
                placeholder="https://..."
              />
              {errors.complaint_url && (
                <span className="pedit__error">
                  <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.complaint_url}
                </span>
              )}
            </div>

            <div className="pedit__field-row">
              <div className="pedit__field">
                <span className="pedit__label">Email de suporte</span>
                <input
                  className="pedit__input"
                  type="email"
                  data-field="support_email"
                  value={form.support_email}
                  onChange={(e) => update('support_email', e.target.value)}
                  placeholder="suporte@seuproduto.com"
                />
                {errors.support_email && (
                  <span className="pedit__error">
                    <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.support_email}
                  </span>
                )}
              </div>
              <div className="pedit__field">
                <span className="pedit__label">Telefone de suporte</span>
                <input
                  className="pedit__input"
                  type="tel"
                  inputMode="numeric"
                  data-field="support_phone"
                  value={form.support_phone}
                  onChange={(e) => {
                    // Aplica máscara: aceita só dígitos, formata (DD) NNNNN-NNNN
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                    let formatted = digits;
                    if (digits.length > 2) formatted = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
                    if (digits.length > 7) formatted = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
                    update('support_phone', formatted);
                  }}
                  placeholder="(11) 99999-9999"
                  maxLength={16}
                />
                {errors.support_phone && (
                  <span className="pedit__error">
                    <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.support_phone}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Comissionamento */}
          <div className="pedit__card">
            <div className="pedit__card-header">
              <div className="pedit__card-icon">
                <Icon icon="mdi:account-multiple-plus-outline" width="20" />
              </div>
              <div className="pedit__card-title-wrap">
                <span className="pedit__card-title">Comissionamento</span>
                <span className="pedit__card-subtitle">Percentual pago a cada papel do time</span>
              </div>
            </div>

            <div className="pedit__field">
              <span className="pedit__label">Tipo de comissão</span>
              <Dropdown
                value={form.commission_type}
                onChange={(v) => update('commission_type', v)}
                options={[
                  { value: 'percentage', label: 'Porcentagem', icon: 'mdi:percent', secondary: '%' },
                  { value: 'fixed', label: 'Valor fixo', icon: 'mdi:currency-brl', secondary: 'R$' },
                ]}
              />
            </div>

            <div className="pedit__field-row3">
              {[
                { key: 'commission_value_affiliate', label: 'Afiliado' },
                { key: 'commission_value_manager', label: 'Gerente' },
                { key: 'commission_value_coproducer', label: 'Coprodutor' },
              ].map((field) => (
                <div key={field.key} className="pedit__field">
                  <span className="pedit__label">{field.label}</span>
                  <div className="pedit__input-suffix">
                    <input
                      className="pedit__input"
                      type="text"
                      inputMode="decimal"
                      data-field={field.key}
                      value={form[field.key] === 0 ? '' : form[field.key]}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
                        const parts = cleaned.split('.');
                        const normalized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : cleaned;
                        update(field.key, normalized);
                      }}
                      placeholder="0"
                    />
                    <span className="pedit__input-suffix-text">
                      {form.commission_type === 'percentage' ? '%' : 'R$'}
                    </span>
                  </div>
                  {errors[field.key] && (
                    <span className="pedit__error">{errors[field.key]}</span>
                  )}
                </div>
              ))}
            </div>

            <div className="pedit__notice pedit__notice--info" style={{ marginTop: 14, marginBottom: 0 }}>
              <Icon icon="mdi:information-outline" width="16" />
              <span>
                Edite afiliados, gerentes e coprodutores individualmente nas seções
                <b> Afiliação</b> e <b>Time</b> da página de detalhes.
              </span>
            </div>
          </div>

          {/* Espaço inferior pra não cobrir o sticky footer */}
          <div style={{ height: 60 }} />
        </div>

        {/* Sticky footer */}
        <div className="pedit__sticky-footer">
          <div className={`pedit__sticky-footer-info ${isDirty ? 'is-dirty' : ''}`}>
            {isDirty ? (
              <>
                <Icon icon="mdi:circle-medium" width="16" />
                Alterações não salvas
              </>
            ) : (
              <>
                <Icon icon="mdi:check-circle-outline" width="16" />
                Tudo salvo
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              className="pedit__btn"
              onClick={() => navigate(`/products/${original.uuid || original.id}`)}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="pedit__btn pedit__btn--primary"
              onClick={handleSave}
              disabled={saving || !isDirty}
            >
              {saving ? (
                <>
                  <Icon icon="mdi:loading" className="spin" width="14" />
                  Salvando...
                </>
              ) : (
                <>
                  <Icon icon="mdi:content-save-outline" width="14" />
                  Salvar alterações
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </MasterLayout>
  );
};

export default ProductEditPage;
